import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import { useAuth } from '../context/AuthContext';
import { getUserProfile, updateUserProfile } from '../services/userProfileService';
import AddressAutocomplete from '../components/common/AddressAutocomplete';
import './ProfilePage.css';

function ProfilePage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const [profile, setProfile] = useState({
    first_name: '',
    last_name: '',
    shipping_address: null
  });
  const [initialProfile, setInitialProfile] = useState({
    first_name: '',
    last_name: '',
    shipping_address: null
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    loadProfile();
  }, [isAuthenticated, navigate]);

  const loadProfile = async () => {
    setLoading(true);
    const { data, error } = await getUserProfile();
    
    if (error) {
      console.error('Error loading profile', error);
      setLoading(false);
      return;
    }

    if (data) {
      const profileData = {
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        shipping_address: data.shipping_address || null
      };
      setProfile(profileData);
      setInitialProfile(profileData);
    }
    setLoading(false);
  };

  const handleFieldBlur = async (field, value) => {
    // Only save if value has changed from initial
    if (field === 'first_name' || field === 'last_name') {
      if (value === (initialProfile[field] || '')) return;
    } else if (field === 'shipping_address') {
      const initAddr = initialProfile.shipping_address;
      const currentAddr = profile.shipping_address;
      if (JSON.stringify(initAddr) === JSON.stringify(currentAddr)) return;
    }

    const updateData = {};
    if (field === 'first_name' || field === 'last_name') {
      updateData[field] = value;
    } else if (field === 'shipping_address') {
      updateData.shipping_address = profile.shipping_address;
    }

    const { success, error } = await updateUserProfile(updateData);
    if (!success || error) {
      console.error('Save error:', error);
      return;
    }

    // Update initial profile after successful save
    setInitialProfile({ ...profile });
  };

  const handleNameChange = (field, e) => {
    const raw = e.target.value || '';
    const lettersOnly = raw.replace(/[^A-Za-z\s]/g, '');
    const limited = lettersOnly.slice(0, 25);
    setProfile({ ...profile, [field]: limited });
  };

  const handleAddressChange = (address) => {
    setProfile({
      ...profile,
      shipping_address: address
    });
  };

  

  // Auto-save address when it changes and is valid
  useEffect(() => {
    if (profile.shipping_address && profile.shipping_address.line1 && profile.shipping_address.city && profile.shipping_address.state && profile.shipping_address.postal_code) {
      // Debounce the save operation
      const timer = setTimeout(() => {
        handleFieldBlur('shipping_address', profile.shipping_address);
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [profile.shipping_address]);

  if (loading) {
    return (
      <div className="profile-page">
        <Navbar />
        <div className="profile-container">
          <div className="loading">Loading profile...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <Navbar />
      <div className="profile-container">
        <h1>My Profile</h1>
        
        <div className="profile-section">
          <h2>Personal Information</h2>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="first_name">First Name</label>
              <div className="input-wrapper">
                <input
                  id="first_name"
                  type="text"
                  value={profile.first_name}
                  onChange={(e) => handleNameChange('first_name', e)}
                  onBlur={(e) => handleFieldBlur('first_name', e.target.value)}
                  placeholder="First name"
                  className="profile-input"
                  maxLength={25}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="last_name">Last Name</label>
              <div className="input-wrapper">
                <input
                  id="last_name"
                  type="text"
                  value={profile.last_name}
                  onChange={(e) => handleNameChange('last_name', e)}
                  onBlur={(e) => handleFieldBlur('last_name', e.target.value)}
                  placeholder="Last name"
                  className="profile-input"
                  maxLength={25}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="profile-section">
          <h2>Shipping Address</h2>
          <div className="address-autocomplete-wrapper">
            <AddressAutocomplete
              value={profile.shipping_address}
              onChange={handleAddressChange}
            />
          </div>
        </div>

        <div className="profile-info">
          <p>Changes are automatically saved when you leave each field.</p>
        </div>
      </div>
    </div>
  );
}

export default ProfilePage;

